import {
  LuUsers,
  LuCheck,
  LuClock,
  LuDollarSign,
  LuSearch,
  LuFilter,
  LuActivity,
  LuLoader,
  LuBuilding2,
  LuFileSpreadsheet,
} from "react-icons/lu";
import React, { useState, useEffect } from "react";
import { db } from "@lib/firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getCountFromServer,
} from "firebase/firestore";
import axios from "axios";
import { InputField } from "@cakrawalajakarta/shared-components";
import { useAuth } from "../../context/AuthContext";

const COLLECTION_NAME = "pendaftaranHotel2026";
const ITEMS_PER_PAGE = 50;

const HOTEL_SPREADSHEET_ID = "1rD3tVmrxE3UO9gPATwC7zKCU3XFQFXfx_AvNHX_VqLI";

export const AdminHotel = () => {
  const { getIdToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [wismaFilter, setWismaFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({ total: 0, settlement: 0, pending: 0, revenue: 0, netRevenue: 0 });

  useEffect(() => {
    fetchOrders("init");
    fetchStats();
  }, [statusFilter, wismaFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrders("init");
    }, 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchStats = async () => {
    try {
      const coll = collection(db, COLLECTION_NAME);
      const [totalSnap, settlementSnap, pendingSnap, paidOrdersSnap] = await Promise.all([
        getCountFromServer(query(coll, where("transaction_status", "!=", "checkout"))),
        getCountFromServer(query(coll, where("transaction_status", "==", "settlement"))),
        getCountFromServer(query(coll, where("transaction_status", "==", "pending"))),
        getDocs(query(coll, where("transaction_status", "in", ["settlement", "capture"]))),
      ]);

      const getRevenueAmount = (order) =>
        Number(order.totalHarga || order.harga || order.gross_amount) || 0;
      const paidOrders = paidOrdersSnap.docs
        .map((doc) => doc.data())
        .filter((order) => !order.financeExcluded && getRevenueAmount(order) > 0);
      const grossRevenue = paidOrders.reduce((sum, order) => {
        return sum + getRevenueAmount(order);
      }, 0);
      const netRevenue = Math.max(0, grossRevenue - (paidOrders.length * 4440));

      setStats({
        total: totalSnap.data().count,
        settlement: settlementSnap.data().count,
        pending: pendingSnap.data().count,
        revenue: grossRevenue,
        netRevenue,
      });
    } catch (err) {
      console.error("fetchStats error:", err);
    }
  };

  const fetchOrders = async (action = "init") => {
    setLoading(true);
    try {
      const constraints = [];

      if (statusFilter !== "all") {
        const dbStatus = statusFilter === "expired" ? "expire" : statusFilter;
        constraints.push(where("transaction_status", "==", dbStatus));
      }
      if (wismaFilter !== "all") {
        constraints.push(where("wisma", "==", wismaFilter));
      }

      if (searchQuery.trim()) {
        const term = searchQuery.trim();
        if (term.toUpperCase().startsWith("HOTEL-")) {
          constraints.push(where("orderId", "==", term));
        } else if (term.includes("@")) {
          constraints.push(where("email", "==", term));
        } else if (/^\d+$/.test(term)) {
          constraints.push(where("telepon", "==", term));
        }
      }

      constraints.push(orderBy("createdAt", "desc"));

      if (action === "next" && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }
      constraints.push(limit(ITEMS_PER_PAGE));

      const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), ...constraints));
      let list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }
      setIsLastPage(snapshot.empty || snapshot.docs.length < ITEMS_PER_PAGE);

      // Exclude 'checkout' from displaying in 'all' view
      if (statusFilter === "all") {
        list = list.filter((order) => order.transaction_status !== "checkout");
      }

      if (action === "next") {
        setOrders((prev) => [...prev, ...list]);
      } else {
        setOrders(list);
      }
    } catch (err) {
      console.error("fetchOrders error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "settlement" || status === "capture")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Paid</span>;
    if (status === "pending")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Pending</span>;
    if (status === "expire")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Expired</span>;
    if (status === "cancel" || status === "deny" || status === "failure")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Failed</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{status || "-"}</span>;
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(value || 0);

  const formatDate = (ts) => {
    if (!ts) return "-";
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const exportToGoogleSheets = async () => {
    if (!confirm("Existing data in the Hotel Google Sheet will be overwritten. Continue?")) return;
    setExporting(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/export-hotel-sheets`,
        { spreadsheetId: HOTEL_SPREADSHEET_ID },
        { headers: { Authorization: `Bearer ${await getIdToken()}` } }
      );
      if (response.data.success) {
        alert("Success! Opening Google Sheets...");
        window.open(response.data.url, "_blank");
      } else if (response.data.message) {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Export to Sheets failed:", error);
      alert("Failed to export to Google Sheets: " + (error.response?.data?.error || error.message));
    } finally {
      setExporting(false);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-lime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
          <LuBuilding2 size={120} />
        </div>
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                <LuActivity size={14} /> Administration
              </p>
              <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Hotel Orders</h1>
              <p className="text-slate-500 text-sm max-w-xl">Manage JOTR 2026 accommodation orders</p>
            </div>
            <div className="flex flex-col sm:flex-row md:justify-end gap-2">
              <button
                onClick={exportToGoogleSheets}
                disabled={exporting}
                className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-green-600 text-white px-4 py-3 md:py-2 rounded-xl hover:bg-green-700 transition-colors text-sm font-medium h-fit disabled:opacity-50"
              >
                {exporting ? <LuLoader className="animate-spin" /> : <LuFileSpreadsheet className="text-white" />}
                Google Sheets
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuDollarSign size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Revenue</p>
          <h3 className="text-2xl font-bold text-slate-800">{formatCurrency(stats.revenue)}</h3>
          <p className="text-sm text-slate-400 mt-1">Net: {formatCurrency(stats.netRevenue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuUsers size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Total</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.total}</h3>
          <p className="text-sm text-slate-400 mt-1">All orders</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuCheck size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Paid</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.settlement}</h3>
          <p className="text-sm text-slate-400 mt-1">Settlement</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuClock size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Pending</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.pending}</h3>
          <p className="text-sm text-slate-400 mt-1">Awaiting payment</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <InputField
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email, Order ID, or phone number..."
              startIcon={<LuSearch className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
          <div className="min-w-[160px]">
            <InputField
              type="select"
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: "all", label: "All Statuses" },
                { value: "settlement", label: "Paid" },
                { value: "pending", label: "Pending" },
                { value: "expired", label: "Expired" },
              ]}
              startIcon={<LuFilter className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
          <div className="min-w-[160px]">
            <InputField
              type="select"
              name="wismaFilter"
              value={wismaFilter}
              onChange={(e) => setWismaFilter(e.target.value)}
              options={[
                { value: "all", label: "All Wisma" },
                { value: "Wisma Berkat", label: "Wisma Berkat" },
                { value: "Wisma Indofood", label: "Wisma Indofood" },
                { value: "Wisma Pondok Pemuda", label: "Wisma Pondok Pemuda" },
              ]}
              startIcon={<LuBuilding2 className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-700">{orders.length}</span> orders
      </p>

      {/* Orders List */}
      <div className="space-y-3">
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No orders found</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}
              >
                {/* Mobile */}
                <div className="lg:hidden flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 text-base truncate">{order.namaLengkap}</p>
                      <p className="text-sm text-slate-500 truncate">{order.email}</p>
                    </div>
                    {getStatusBadge(order.transaction_status)}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="font-mono">{order.orderId || order.id}</span>
                    <span className="text-slate-400">{order.wisma}</span>
                    <span>{order.tipeKamar}</span>
                    <span>{formatCurrency(order.totalHarga || order.gross_amount)}</span>
                  </div>
                </div>

                {/* Desktop */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <p className="font-semibold text-slate-800 text-sm truncate">{order.namaLengkap}</p>
                    <p className="text-xs text-slate-500 truncate">{order.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">Wisma</p>
                    <p className="text-sm text-slate-700 truncate">{order.wisma || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">Room Type</p>
                    <p className="text-sm text-slate-700 truncate">{order.tipeKamar || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">Check-in / Nights</p>
                    <p className="text-sm text-slate-700">{order.checkInDate || "-"} ({order.jumlahMalam || 1}N)</p>
                  </div>
                  <div className="col-span-1 text-right">
                    <p className="text-sm font-bold text-slate-800">{formatCurrency(order.totalHarga || order.gross_amount)}</p>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    {getStatusBadge(order.transaction_status)}
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedRow === order.id && (
                <div className="border-t border-slate-200 bg-slate-50 p-4">
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Order Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: "Order ID", value: order.orderId || order.id },
                        { label: "Full Name", value: order.namaLengkap },
                        { label: "Email", value: order.email },
                        { label: "Phone", value: order.telepon },
                        { label: "Wisma", value: order.wisma },
                        { label: "Room Type", value: order.tipeKamar },
                        { label: "Check-in Date", value: order.checkInDate },
                        { label: "Total Nights", value: order.jumlahMalam ? `${order.jumlahMalam} nights` : "-" },
                        { label: "Capacity", value: order.kapasitas },
                        { label: "Total", value: formatCurrency(order.totalHarga || order.gross_amount) },
                        { label: "Status", value: order.transaction_status },
                        { label: "Created At", value: formatDate(order.createdAt) },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-slate-500 text-xs uppercase mb-1">{label}</p>
                          <p className="text-slate-800 text-sm font-medium break-words">{value || "-"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {!isLastPage && (
        <div className="flex justify-end mt-4">
          <button
            onClick={() => fetchOrders("next")}
            disabled={loading}
            className="px-6 py-2 rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {loading ? <LuLoader className="animate-spin" /> : null}
            Load More
          </button>
        </div>
      )}
    </div>
  );
};
